import React, { useCallback } from 'react';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { EditorToolbarProvider, EditorToolbarUIProvider } from '@atlaskit/editor-common/toolbar';
import { Popup } from '@atlaskit/editor-common/ui';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { calculateToolbarPositionTrackHead, calculateToolbarPositionOnCellSelection } from '@atlaskit/editor-common/utils';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { ToolbarSection, ToolbarButtonGroup, ToolbarDropdownItemSection } from '@atlaskit/editor-toolbar';
import { ToolbarModelRenderer } from '@atlaskit/editor-toolbar-model';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { SELECTION_TOOLBAR_LABEL } from '../consts';
var isToolbarComponent = function isToolbarComponent(component) {
  return component.type === 'toolbar' && component.key === 'inline-text-toolbar';
};
export var SelectionToolbar = function SelectionToolbar(_ref) {
  var _api$toolbar, _api$toolbar$getCompo;
  var api = _ref.api,
    editorView = _ref.editorView,
    mountPoint = _ref.mountPoint,
    disableSelectionToolbarWhenPinned = _ref.disableSelectionToolbarWhenPinned;
  var _useSharedPluginState = useSharedPluginStateWithSelector(api, ['toolbar'], function (state) {
      var _state$toolbarState;
      return {
        shouldShowToolbar: (_state$toolbarState = state.toolbarState) === null || _state$toolbarState === void 0 ? void 0 : _state$toolbarState.shouldShowToolbar
      };
    }),
    shouldShowToolbar = _useSharedPluginState.shouldShowToolbar;
  var components = api === null || api === void 0 || (_api$toolbar = api.toolbar) === null || _api$toolbar === void 0 || (_api$toolbar = _api$toolbar.actions) === null || _api$toolbar === void 0 || (_api$toolbar$getCompo = _api$toolbar.getComponents) === null || _api$toolbar$getCompo === void 0 ? void 0 : _api$toolbar$getCompo.call(_api$toolbar);
  var toolbar = components === null || components === void 0 ? void 0 : components.find(function (component) {
    return isToolbarComponent(component);
  });
  var currentUserIntent = useSharedPluginStateSelector(api, 'userIntent.currentUserIntent');
  var connectivityStateMode = useSharedPluginStateSelector(api, 'connectivity.mode');
  var isOffline = connectivityStateMode === 'offline';
  // TODO: ED-28735 - figure out a better way to control this - needed to re-render on selection change
  // @ts-expect-error
  var selection = useSharedPluginStateSelector(api, 'selection.selection');
  var isTextSelection = !editorView.state.selection.empty && editorView.state.selection instanceof TextSelection;
  var isCellSelection = !editorView.state.selection.empty && '$anchorCell' in editorView.state.selection;
  var toolbarDocking = useSharedPluginStateSelector(api, 'userPreferences.preferences.toolbarDockingPosition');
  var onPositionCalculated = useCallback(function (position) {
    var toolbarTitle = SELECTION_TOOLBAR_LABEL;

    // Show special position on cell selection only when editor controls experiment is enabled
    var isEditorControlsEnabled = expValEquals('platform_editor_controls', 'cohort', 'variant1');
    var isCellSelection = ('$anchorCell' in editorView.state.selection);
    if (isCellSelection && isEditorControlsEnabled) {
      return calculateToolbarPositionOnCellSelection(toolbarTitle)(editorView, position);
    }
    var calc = calculateToolbarPositionTrackHead;
    return calc(toolbarTitle)(editorView, position);
  }, [editorView]);
  if (expValEquals('platform_editor_toolbar_aifc_template_editor', 'isEnabled', true) && toolbarDocking === 'top' && disableSelectionToolbarWhenPinned || !components || !toolbar) {
    return null;
  }
  if (!(isTextSelection || isCellSelection) || currentUserIntent === 'dragging' || !shouldShowToolbar || currentUserIntent === 'blockMenuOpen' && expValEqualsNoExposure('platform_editor_block_menu', 'isEnabled', true) || isSSR()) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Popup, {
    offset: [0, 10],
    target: getDomRefFromSelection(editorView),
    onPositionCalculated: onPositionCalculated,
    mountTo: mountPoint
  }, /*#__PURE__*/React.createElement(EditorToolbarProvider, {
    editorView: editorView
  }, /*#__PURE__*/React.createElement(EditorToolbarUIProvider, {
    api: api,
    isDisabled: isOffline
  }, /*#__PURE__*/React.createElement(ToolbarModelRenderer, {
    toolbar: toolbar,
    components: components,
    fallbacks: {
      section: ToolbarSection,
      menuSection: ToolbarDropdownItemSection,
      group: ToolbarButtonGroup
    }
  }))));
};
var getDomRefFromSelection = function getDomRefFromSelection(view
// dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
) {
  try {
    // Ignored via go/ees005
    // eslint-disable-next-line @atlaskit/editor/no-as-casting
    return findDomRefAtPos(view.state.selection.from, view.domAtPos.bind(view));
    // Ignored via go/ees005
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error) {
    // // eslint-disable-next-line no-console
    // console.warn(error);
    // if (dispatchAnalyticsEvent) {
    // 	const payload: AnalyticsEventPayload = {
    // 		action: ACTION.ERRORED,
    // 		actionSubject: ACTION_SUBJECT.CONTENT_COMPONENT,
    // 		eventType: EVENT_TYPE.OPERATIONAL,
    // 		attributes: {
    // 			component: CONTENT_COMPONENT.FLOATING_TOOLBAR,
    // 			selection: view.state.selection.toJSON(),
    // 			position: view.state.selection.from,
    // 			docSize: view.state.doc.nodeSize,
    // 			error: error.toString(),
    // 			// @ts-expect-error - Object literal may only specify known properties, 'errorStack' does not exist in type
    // 			// This error was introduced after upgrading to TypeScript 5
    // 			errorStack: error.stack || undefined,
    // 		},
    // 	};
    // 	dispatchAnalyticsEvent(payload);
    // }
  }
};